import React, { useEffect, useMemo, useState } from 'react';
import { businessLabels } from '@travel/design-tokens';

import { ApiError, isRecord, textOf, unwrapList, unwrapRecord } from './api-client';
import type { AdminApiClient, JsonRecord } from './api-client';
import { ActionButton, ConfirmDialog, Notice } from './admin-feedback';
import { ProductThumbnail } from './product-thumbnail';
import { AUTHORIZED_SCOPE } from './authorized-scope-client';
import './admin-base-layout.css';

/** Configuration needs an explicit page filter, never the first accessible app. */
export function configurationScopeAppId(api: AdminApiClient, targetAppId: string): string {
  if (targetAppId && targetAppId !== AUTHORIZED_SCOPE) return targetAppId;
  const scope: unknown =
    'getPageScope' in api && typeof api.getPageScope === 'function'
      ? api.getPageScope()
      : undefined;
  return isRecord(scope) && typeof scope.appId === 'string' && scope.appId !== AUTHORIZED_SCOPE
    ? scope.appId
    : '';
}

type LoadState<T> = {
  data?: T;
  error?: unknown;
  loading: boolean;
  reload: () => void;
};

function useTask<T>(key: string, loader: () => Promise<T>, dependency: unknown): LoadState<T> {
  const [revision, setRevision] = useState(0);
  const requestKey = `${key}\u0000${revision}`;
  const [state, setState] = useState<Omit<LoadState<T>, 'reload'> & { key: string }>({
    key: requestKey,
    loading: true,
  });
  useEffect(() => {
    let active = true;
    setState({ key: requestKey, loading: true });
    void loader().then(
      (data) => active && setState({ key: requestKey, loading: false, data }),
      (error: unknown) => active && setState({ key: requestKey, loading: false, error }),
    );
    return () => {
      active = false;
    };
  }, [requestKey, dependency]);
  const visibleState = state.key === requestKey ? state : { key: requestKey, loading: true };
  return { ...visibleState, reload: () => setRevision((value) => value + 1) };
}

function useApi(api: AdminApiClient, path: string | undefined) {
  return useTask(
    path ?? 'canonical-disabled',
    () => (path ? api.get<unknown>(path) : Promise.resolve({ items: [] })),
    api,
  );
}

function stateKind(state: LoadState<unknown>, count: number) {
  if (state.loading) return 'loading';
  if (state.error instanceof ApiError && state.error.isPermissionError) return 'permission';
  if (state.error) return 'error';
  return count === 0 ? 'empty' : 'ready';
}

function CanonicalState({
  state,
  count,
  noun,
  allowEmpty = false,
  children,
}: {
  state: LoadState<unknown>;
  count: number;
  noun: string;
  allowEmpty?: boolean;
  children: React.ReactNode;
}) {
  const kind = stateKind(state, count);
  if (kind === 'ready' || (kind === 'empty' && allowEmpty)) return <>{children}</>;
  const copy = {
    loading: ['正在加载', `正在读取${noun}，请稍候…`],
    permission: ['无权访问', `当前账号不能查看这些${noun}，请联系管理员调整权限。`],
    error: ['加载失败', `${noun}暂时无法读取，请稍后重试。`],
    empty: [`暂无${noun}`, `当前筛选条件下还没有${noun}。`],
  }[kind] ?? ['暂不可用', '请稍后重试。'];
  return (
    <div
      className={`canonical-state canonical-state--${kind}`}
      role={kind === 'error' ? 'alert' : undefined}
    >
      <span className={kind === 'loading' ? 'canonical-spinner' : 'canonical-state__icon'}>
        {kind === 'permission' ? '锁' : kind === 'empty' ? '空' : kind === 'error' ? '!' : ''}
      </span>
      <strong>{copy[0]}</strong>
      <p>{copy[1]}</p>
      {kind !== 'loading' && <button onClick={state.reload}>重新加载</button>}
    </div>
  );
}

function PageTitle({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="canonical-page-title">
      <div>
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>
      {actions && <div className="canonical-page-actions">{actions}</div>}
    </div>
  );
}

const STATUS_LABELS: Record<string, string> = {
  ...businessLabels.orderStatus,
  ACTIVE: '已启用',
  DISABLED: '已停用',
  DRAFT: '草稿',
  PENDING_PAYMENT: '待支付',
  FULFILLING: '服务进行中',
  OPEN: '待处理',
  ACKNOWLEDGED: '已知悉',
  RESOLVED: '已解决',
};

function statusLabel(value: unknown) {
  return typeof value === 'string' ? (STATUS_LABELS[value] ?? '待处理') : '待处理';
}

function statusTone(value: unknown) {
  if (value === 'ACTIVE' || value === 'COMPLETED') return 'green';
  if (value === 'DISABLED' || value === 'CANCELLED' || value === 'REFUNDED') return 'gray';
  if (value === 'REFUNDING') return 'red';
  return 'orange';
}

function formatMoney(value: unknown) {
  const cents = Number(value);
  if (!Number.isFinite(cents)) return '暂不可用';
  return `¥${(cents / 100).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(value: unknown, fallback = '—') {
  if (typeof value !== 'string' && !(value instanceof Date)) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function recordArray(value: unknown) {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function childRecord(value: unknown) {
  return isRecord(value) ? value : {};
}

function humanText(record: JsonRecord, ...keys: string[]) {
  const value = textOf(record, ...keys);
  return value === '—' ? '暂未配置' : value;
}

function cleanFixtureLabel(value: string) {
  return value.replace(/\s*[（(]DEV\/TEST(?: Fixture)?[）)]/giu, '').trim();
}

function displayBusinessCode(record: JsonRecord, key: string) {
  const value = humanText(record, key);
  return value === '暂未配置' ? value : value.replace(/^DEV-/u, '');
}

interface CanonicalPageProps {
  api: AdminApiClient;
  targetAppId: string;
}

function DashboardMetric({
  icon,
  label,
  value,
  note,
  tone,
}: {
  icon: string;
  label: string;
  value: string | number;
  note: string;
  tone: string;
}) {
  return (
    <article className="dashboard-metric">
      <span className={`dashboard-metric__icon dashboard-metric__icon--${tone}`}>{icon}</span>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{note}</small>
      </div>
    </article>
  );
}

function cityNameMap(cities: JsonRecord[]) {
  return new Map(cities.map((city) => [textOf(city, 'id'), humanText(city, 'name')]));
}

function orderProductName(order: JsonRecord) {
  const items = recordArray(order.items);
  const first = items[0] ?? {};
  const nested = childRecord(first.productSnapshot);
  const name = textOf(order, 'productTitle', 'productName');
  if (name !== '—') return name;
  const itemName = textOf(first, 'productTitleSnapshot', 'productTitle', 'title', 'name');
  if (itemName !== '—') return itemName;
  const nestedName = textOf(nested, 'title', 'name');
  return nestedName === '—' ? '旅游服务订单' : nestedName;
}

export function CanonicalDashboardPage({ api, targetAppId }: CanonicalPageProps) {
  const configurationAppId = configurationScopeAppId(api, targetAppId);
  const aggregate = targetAppId === AUTHORIZED_SCOPE;
  const encoded = targetAppId ? encodeURIComponent(targetAppId) : '';
  const orderState = useApi(api, targetAppId ? `/api/admin/m4/orders?appId=${encoded}` : undefined);
  const cityState = useApi(api, '/api/admin/m2/cities');
  const alertState = useApi(
    api,
    configurationAppId
      ? `/api/admin/m8/apps/${encodeURIComponent(configurationAppId)}/ops/snapshot`
      : undefined,
  );
  const orders = unwrapList(orderState.data);
  const cities = unwrapList(cityState.data);
  const names = useMemo(() => cityNameMap(cities), [cities]);
  const completed = orders.filter((order) => textOf(order, 'status') === 'COMPLETED').length;
  const pendingConfirm = orders.filter((order) =>
    ['PENDING_CONFIRM', 'PAID_INFO_READY'].includes(textOf(order, 'status')),
  ).length;
  const pendingFulfillment = orders.filter(
    (order) => textOf(order, 'status') === 'CONFIRMED',
  ).length;
  const amountOrders = orders.filter(
    (order) => typeof order.payAmountCents === 'number' && Number.isFinite(order.payAmountCents),
  );
  const payable = amountOrders.reduce((sum, order) => sum + Number(order.payAmountCents), 0);
  const refundOrders = orders.filter(
    (order) =>
      typeof order.refundAmountCents === 'number' && Number.isFinite(order.refundAmountCents),
  );
  const refunded = refundOrders.reduce((sum, order) => sum + Number(order.refundAmountCents), 0);
  const dayBuckets = Array.from({ length: 7 }, (_, offset) => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - (6 - offset));
    const next = new Date(date);
    next.setDate(next.getDate() + 1);
    return {
      label: `${date.getMonth() + 1}/${date.getDate()}`,
      value: orders.filter((order) => {
        const created = new Date(String(order.createdAt ?? ''));
        return created >= date && created < next;
      }).length,
    };
  });
  const maxDay = Math.max(1, ...dayBuckets.map((item) => item.value));
  const byCity = [
    ...amountOrders.reduce((map, order) => {
      const cityId = textOf(order, 'sourceCityId', 'cityId');
      const label = names.get(cityId) ?? '未标记城市';
      map.set(label, (map.get(label) ?? 0) + Number(order.payAmountCents ?? 0));
      return map;
    }, new Map<string, number>()),
  ]
    .sort((left, right) => right[1] - left[1])
    .slice(0, 5);
  const maximumCity = Math.max(1, ...byCity.map((entry) => entry[1]));
  const alerts = unwrapList(childRecord(childRecord(alertState.data).durable).alerts);
  const bySource = [
    ...orders.reduce((map, order) => {
      const source = childRecord(order.sourceChannel);
      const sourceName = textOf(source, 'name');
      const label = sourceName === '—' ? '自然访问' : cleanFixtureLabel(sourceName);
      map.set(label, (map.get(label) ?? 0) + 1);
      return map;
    }, new Map<string, number>()),
  ].sort((left, right) => right[1] - left[1]);

  if (!targetAppId) {
    return (
      <>
        <PageTitle title="经营总览" subtitle="请选择小程序后查看相应经营数据" />
        <div className="canonical-state canonical-state--empty">
          <span className="canonical-state__icon">选</span>
          <strong>请选择小程序</strong>
          <p>使用页面右上角的小程序选择器确定经营数据范围。</p>
        </div>
      </>
    );
  }

  return (
    <div className="canonical-dashboard">
      <PageTitle
        title="经营总览"
        subtitle="仅展示当前账号有权查看的小程序与城市数据"
        actions={
          <>
            <span className="canonical-action-hint">
              当前授权范围 · {aggregate ? '本页订单概览' : '近 7 日趋势'}
            </span>
            <button
              className="canonical-primary"
              onClick={() => {
                orderState.reload();
                cityState.reload();
                alertState.reload();
              }}
            >
              刷新数据
            </button>
          </>
        }
      />
      {aggregate && (
        <Notice>
          金额、状态分组与趋势仅基于本页返回的订单，不代表全部授权订单汇总。
          {!configurationAppId ? '运行告警属于具体小程序，请在本页筛选区选择小程序后读取。' : ''}
        </Notice>
      )}
      {Boolean(alertState.error) && (
        <Notice tone="warning">
          运行告警读取失败，未计入告警数量；订单列表与金额仍按其真实读取结果展示。
        </Notice>
      )}
      <CanonicalState state={orderState} count={orders.length} noun="经营数据" allowEmpty>
        <section className="dashboard-metrics" aria-label="经营指标">
          <DashboardMetric
            icon="¥"
            label={aggregate ? '本页订单应付合计' : '订单应付合计'}
            value={
              amountOrders.length ? formatMoney(payable) : orders.length ? '无权限查看' : '¥0.00'
            }
            note={amountOrders.length ? '不是实收或利润口径' : '当前授权范围暂无可统计订单'}
            tone="green"
          />
          <DashboardMetric
            icon="单"
            label={aggregate ? '本页订单' : '当前订单'}
            value={orders.length}
            note="当前授权范围"
            tone="blue"
          />
          <DashboardMetric
            icon="确"
            label="待确认"
            value={pendingConfirm}
            note="需要运营处理"
            tone="purple"
          />
          <DashboardMetric
            icon="履"
            label="待开始履约"
            value={pendingFulfillment}
            note="已确认，等待安排服务"
            tone="orange"
          />
          <DashboardMetric
            icon="成"
            label="已完成"
            value={completed}
            note="服务闭环订单"
            tone="green"
          />
          <DashboardMetric
            icon="退"
            label="退款金额"
            value={
              refundOrders.length
                ? formatMoney(refunded)
                : orders.length
                  ? '无权限查看'
                  : formatMoney(0)
            }
            note="仅在具备 L2 字段权限时汇总"
            tone="pink"
          />
        </section>

        <nav className="dashboard-work-queue" aria-label="运营待办">
          <strong>运营待办</strong>
          <a href="/admin/orders/pending-confirm">
            待确认订单 <b>{pendingConfirm}</b>
          </a>
          <a href="/admin/orders?status=CONFIRMED">
            待开始履约 <b>{pendingFulfillment}</b>
          </a>
          <a href="/admin/orders/need-trip-info">
            待补资料{' '}
            <b>
              {orders.filter((order) => textOf(order, 'status') === 'PAID_NEED_TRIP_INFO').length}
            </b>
          </a>
          <a href="/admin/refunds">退款申请</a>
          <a href="/admin/after-sales">售后工单</a>
        </nav>
        <section className="dashboard-bottom-grid">
          <article className="canonical-panel dashboard-latest">
            <header>
              <h2>最新订单</h2>
              <a href="/admin/orders">进入中央订单池 →</a>
            </header>
            <div className="canonical-table-wrap">
              <table className="canonical-business-table">
                <thead>
                  <tr>
                    <th>订单</th>
                    <th>线路</th>
                    <th>城市</th>
                    <th>应付</th>
                    <th>状态</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.slice(0, 14).map((order) => (
                    <tr key={textOf(order, 'id', 'orderNo')}>
                      <td>{humanText(order, 'orderNo')}</td>
                      <td>{orderProductName(order)}</td>
                      <td>{names.get(textOf(order, 'sourceCityId', 'cityId')) ?? '未标记城市'}</td>
                      <td>{formatMoney(order.payAmountCents)}</td>
                      <td>
                        <span className={`canonical-pill ${statusTone(order.status)}`}>
                          {statusLabel(order.status)}
                        </span>
                      </td>
                      <td>
                        <a href={`/admin/orders/${encodeURIComponent(textOf(order, 'id'))}`}>
                          查看
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>
          <article className="canonical-panel dashboard-alerts">
            <h2>系统告警</h2>
            {alertState.loading ? (
              <p className="muted">正在加载告警…</p>
            ) : alertState.error ? (
              <p role="alert">
                告警暂时无法读取，
                <button type="button" onClick={alertState.reload}>
                  重新加载
                </button>
              </p>
            ) : alerts.length ? (
              alerts.slice(0, 3).map((alert) => (
                <div key={textOf(alert, 'id', 'openedAt')}>
                  <strong>{humanText(alert, 'title', 'name', 'metric')}</strong>
                  <span>{formatDate(alert.openedAt, '刚刚')}</span>
                  <p>{humanText(alert, 'message', 'summary')}</p>
                </div>
              ))
            ) : (
              <div className="canonical-unavailable compact">
                <p>当前没有未处理告警</p>
              </div>
            )}
          </article>
        </section>
        <section className="dashboard-analysis-grid">
          <article className="canonical-panel dashboard-trend">
            <h2>近 7 日订单趋势</h2>
            {dayBuckets.every((item) => item.value === 0) ? (
              <p className="dashboard-trend-empty">近 7 日暂无新订单</p>
            ) : (
              <div className="dashboard-bars" aria-label="近七日订单数量">
                {dayBuckets.map((item) => (
                  <div key={item.label}>
                    <span
                      title={`${item.value} 单`}
                      style={{
                        height: `${(item.value / maxDay) * 100}%`,
                        minHeight: item.value ? 5 : 0,
                      }}
                    />
                    <small>{item.label}</small>
                  </div>
                ))}
              </div>
            )}
          </article>
          <article className="canonical-panel dashboard-source">
            <h2>订单来源占比</h2>
            {bySource.length ? (
              bySource.map(([name, count]) => (
                <div className="dashboard-rank-row" key={name}>
                  <div>
                    <b>{name}</b>
                    <strong>{count} 单</strong>
                  </div>
                  <i>
                    <span style={{ width: `${(count / Math.max(1, orders.length)) * 100}%` }} />
                  </i>
                </div>
              ))
            ) : (
              <div className="canonical-unavailable compact">
                <p>当前范围暂无订单来源数据</p>
              </div>
            )}
          </article>
          <article className="canonical-panel dashboard-city-rank">
            <h2>城市订单金额 TOP5</h2>
            {byCity.length ? (
              byCity.map(([name, amount], index) => (
                <div className="dashboard-rank-row" key={name}>
                  <div>
                    <span>{index + 1}</span>
                    <b>{name}</b>
                    <strong>{formatMoney(amount)}</strong>
                  </div>
                  <i>
                    <span style={{ width: `${(amount / maximumCity) * 100}%` }} />
                  </i>
                </div>
              ))
            ) : (
              <div className="canonical-unavailable compact">
                <p>暂无可汇总的城市订单</p>
              </div>
            )}
          </article>
        </section>
      </CanonicalState>
    </div>
  );
}

function CityCreateDrawer({
  api,
  appId,
  regions,
  close,
  done,
}: {
  api: AdminApiClient;
  appId: string;
  regions: JsonRecord[];
  close: () => void;
  done: () => void;
}) {
  const [regionId, setRegionId] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  return (
    <div className="canonical-drawer" role="dialog" aria-modal="true" aria-label="新增城市">
      <div className="canonical-drawer__backdrop" onClick={close} />
      <section>
        <header>
          <h2>新增城市</h2>
          <button aria-label="关闭" onClick={close}>
            ×
          </button>
        </header>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setBusy(true);
            setResult('');
            void api
              .send(`/api/admin/m2/apps/${encodeURIComponent(appId)}/cities`, 'POST', {
                regionId,
                code,
                name,
                sortOrder: Number(sortOrder),
              })
              .then(
                () => {
                  setResult('城市已创建，可继续配置接送区域、小程序开放范围和产品。');
                  done();
                },
                (error: unknown) => setResult(error instanceof Error ? error.message : '创建失败'),
              )
              .finally(() => setBusy(false));
          }}
        >
          <label>
            所属地区
            <select required value={regionId} onChange={(event) => setRegionId(event.target.value)}>
              <option value="">请选择地区</option>
              {regions.map((item) => (
                <option key={textOf(item, 'id')} value={textOf(item, 'id')}>
                  {humanText(item, 'name')}
                </option>
              ))}
            </select>
          </label>
          <label>
            城市编码
            <input
              required
              maxLength={50}
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              placeholder="例如 KM"
            />
          </label>
          <label>
            城市名称
            <input
              required
              maxLength={100}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="例如 昆明"
            />
          </label>
          <label>
            前端基础排序
            <input
              required
              type="number"
              min="0"
              value={sortOrder}
              onChange={(event) => setSortOrder(event.target.value)}
            />
          </label>
          <div className="canonical-form-actions">
            <button type="button" onClick={close}>
              取消
            </button>
            <button className="canonical-primary" disabled={busy}>
              {busy ? '创建中…' : '创建城市'}
            </button>
          </div>
          {result ? (
            <p className="canonical-form-result" role="status">
              {result}
            </p>
          ) : null}
        </form>
      </section>
    </div>
  );
}

function CityImportDrawer({
  api,
  appId,
  regions,
  close,
  done,
}: {
  api: AdminApiClient;
  appId: string;
  regions: JsonRecord[];
  close: () => void;
  done: () => void;
}) {
  const [csv, setCsv] = useState('region_code,city_code,city_name,sort_order\n');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  const regionByCode = new Map(
    regions.map((item) => [textOf(item, 'code').toUpperCase(), textOf(item, 'id')]),
  );
  return (
    <div className="canonical-drawer" role="dialog" aria-modal="true" aria-label="导入城市">
      <div className="canonical-drawer__backdrop" onClick={close} />
      <section>
        <header>
          <h2>批量导入城市</h2>
          <button aria-label="关闭" onClick={close}>
            ×
          </button>
        </header>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setResult('');
            let items: { regionId: string; code: string; name: string; sortOrder: number }[];
            try {
              const lines = csv
                .split(/\r?\n/u)
                .map((line) => line.trim())
                .filter(Boolean);
              const dataLines = lines[0]?.toLowerCase().startsWith('region_code')
                ? lines.slice(1)
                : lines;
              items = dataLines.map((line, index) => {
                const [regionCode = '', cityCode = '', cityName = '', order = '0'] = line
                  .split(',')
                  .map((cell) => cell.trim());
                const mappedRegionId = regionByCode.get(regionCode.toUpperCase());
                if (!mappedRegionId || !cityCode || !cityName || !/^\d+$/u.test(order))
                  throw new Error(`第 ${index + 1} 行格式或地区编码无效`);
                return {
                  regionId: mappedRegionId,
                  code: cityCode.toUpperCase(),
                  name: cityName,
                  sortOrder: Number(order),
                };
              });
              if (!items.length) throw new Error('请至少填写一行城市数据');
            } catch (error) {
              setResult(error instanceof Error ? error.message : 'CSV 格式无效');
              return;
            }
            setBusy(true);
            void api
              .send(`/api/admin/m2/apps/${encodeURIComponent(appId)}/cities/import`, 'POST', {
                items,
              })
              .then(
                () => {
                  setResult(`已原子导入 ${items.length} 个城市`);
                  done();
                },
                (error: unknown) => setResult(error instanceof Error ? error.message : '导入失败'),
              )
              .finally(() => setBusy(false));
          }}
        >
          <p className="canonical-rule-note">
            CSV 列：region_code, city_code, city_name, sort_order。任一行失败时整批不写入。
          </p>
          <textarea rows={12} value={csv} onChange={(event) => setCsv(event.target.value)} />
          <div className="canonical-form-actions">
            <button type="button" onClick={close}>
              取消
            </button>
            <button className="canonical-primary" disabled={busy}>
              {busy ? '导入中…' : '校验并导入'}
            </button>
          </div>
          {result ? (
            <p className="canonical-form-result" role="status">
              {result}
            </p>
          ) : null}
        </form>
      </section>
    </div>
  );
}

function CitySortDrawer({
  api,
  cities,
  close,
  done,
}: {
  api: AdminApiClient;
  cities: JsonRecord[];
  close: () => void;
  done: () => void;
}) {
  const [orders, setOrders] = useState(() =>
    Object.fromEntries(cities.map((city) => [textOf(city, 'id'), Number(city.sortOrder ?? 0)])),
  );
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  return (
    <div className="canonical-drawer" role="dialog" aria-modal="true" aria-label="批量排序城市">
      <div className="canonical-drawer__backdrop" onClick={close} />
      <section>
        <header>
          <h2>批量排序城市</h2>
          <button aria-label="关闭" onClick={close}>
            ×
          </button>
        </header>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setBusy(true);
            setResult('');
            const items = cities.map((city) => ({
              cityId: textOf(city, 'id'),
              sortOrder: orders[textOf(city, 'id')] ?? 0,
            }));
            void api
              .send('/api/admin/m2/cities/reorder', 'POST', { items })
              .then(
                () => {
                  setResult('城市基础排序已原子保存');
                  done();
                },
                (error: unknown) => setResult(error instanceof Error ? error.message : '保存失败'),
              )
              .finally(() => setBusy(false));
          }}
        >
          <p className="canonical-rule-note">
            此处维护中央城市基础顺序；各小程序仍可在开放城市配置中单独覆盖顺序。
          </p>
          {cities.map((city) => (
            <label key={textOf(city, 'id')}>
              {humanText(city, 'name')}
              <input
                type="number"
                min="0"
                value={orders[textOf(city, 'id')] ?? 0}
                onChange={(event) =>
                  setOrders({ ...orders, [textOf(city, 'id')]: Number(event.target.value) })
                }
              />
            </label>
          ))}
          <div className="canonical-form-actions">
            <button type="button" onClick={close}>
              取消
            </button>
            <button className="canonical-primary" disabled={busy}>
              {busy ? '保存中…' : '保存排序'}
            </button>
          </div>
          {result ? (
            <p className="canonical-form-result" role="status">
              {result}
            </p>
          ) : null}
        </form>
      </section>
    </div>
  );
}

export function CanonicalCityListPage({ api, targetAppId }: CanonicalPageProps) {
  const access = useApi(api, '/api/admin/m8/security/me');
  const permissionCodes = unwrapRecord(access.data).permissionCodes;
  const canManage = Array.isArray(permissionCodes) && permissionCodes.includes('city.manage');
  const canViewProducts =
    Array.isArray(permissionCodes) && permissionCodes.includes('product.view');
  const cityState = useApi(api, '/api/admin/m2/cities');
  const productState = useApi(api, canViewProducts ? '/api/admin/m2/products' : undefined);
  const appState = useApi(api, '/api/admin/m2/apps');
  const regionState = useApi(
    api,
    targetAppId ? `/api/admin/m2/apps/${encodeURIComponent(targetAppId)}/regions` : undefined,
  );
  const cities = unwrapList(cityState.data);
  const products = unwrapList(productState.data);
  const apps = unwrapList(appState.data);
  const regions = unwrapList(regionState.data);
  const appKey = apps.map((app) => textOf(app, 'id')).join(',');
  const matrixState = useTask(
    `canonical-city-app-matrix:${appKey}`,
    async () =>
      Promise.all(
        apps.map((app) =>
          api.get<unknown>(`/api/admin/m2/apps/${encodeURIComponent(textOf(app, 'id'))}`),
        ),
      ),
    api,
  );
  const appDetails = Array.isArray(matrixState.data) ? matrixState.data.filter(isRecord) : [];
  const [keyword, setKeyword] = useState('');
  const [region, setRegion] = useState('');
  const [status, setStatus] = useState('');
  const [defaultOnly, setDefaultOnly] = useState('');
  const [cityPage, setCityPage] = useState(1);
  const [cityPageSize, setCityPageSize] = useState(14);
  const [applied, setApplied] = useState({ keyword: '', region: '', status: '', defaultOnly: '' });
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showSort, setShowSort] = useState(false);
  const defaultCityIds = new Set(
    appDetails
      .map((detail) => textOf(childRecord(detail.runtime), 'defaultCityId'))
      .filter((id) => id !== '—'),
  );
  const regionOptions = [
    ...new Set(
      cities.map((city) => textOf(childRecord(city.region), 'name')).filter((name) => name !== '—'),
    ),
  ];
  const filtered = cities.filter((city) => {
    const haystack = [city.name, city.code, childRecord(city.region).name]
      .join(' ')
      .toLocaleLowerCase();
    const cityId = textOf(city, 'id');
    return (
      (!applied.keyword || haystack.includes(applied.keyword.toLocaleLowerCase())) &&
      (!applied.region || textOf(childRecord(city.region), 'name') === applied.region) &&
      (!applied.status || textOf(city, 'status') === applied.status) &&
      (!applied.defaultOnly ||
        (applied.defaultOnly === 'YES' ? defaultCityIds.has(cityId) : !defaultCityIds.has(cityId)))
    );
  });

  const cityPageCount = Math.max(1, Math.ceil(filtered.length / cityPageSize));
  const currentCityPage = Math.min(cityPage, cityPageCount);
  const reset = () => {
    setCityPage(1);
    setKeyword('');
    setRegion('');
    setStatus('');
    setDefaultOnly('');
    setApplied({ keyword: '', region: '', status: '', defaultOnly: '' });
  };

  return (
    <div className="canonical-list-page canonical-city-page">
      <PageTitle
        title="城市列表"
        subtitle="城市与地区 / 城市列表 · 城市是可扩展的运营主数据"
        actions={
          <>
            {canManage && <button onClick={() => setShowImport(true)}>导入城市</button>}
            {canManage && <button onClick={() => setShowSort(true)}>批量排序</button>}
            {canManage && (
              <button className="canonical-primary" onClick={() => setShowCreate(true)}>
                ＋ 新增城市
              </button>
            )}
          </>
        }
      />
      <section className="canonical-filter-card">
        <label className="canonical-search">
          <span>搜索</span>
          <input
            aria-label="搜索城市"
            placeholder="城市名称 / 编码 / 省份"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
          />
        </label>
        <select
          aria-label="所属省份"
          value={region}
          onChange={(event) => setRegion(event.target.value)}
        >
          <option value="">所属省份：全部</option>
          {regionOptions.map((name) => (
            <option key={name} value={name}>
              {cleanFixtureLabel(name)}
            </option>
          ))}
        </select>
        <select
          aria-label="状态"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="">状态：全部</option>
          <option value="ACTIVE">已启用</option>
          <option value="DISABLED">已停用</option>
        </select>
        <select
          aria-label="首页默认城市"
          value={defaultOnly}
          onChange={(event) => setDefaultOnly(event.target.value)}
        >
          <option value="">首页默认城市：全部</option>
          <option value="YES">已设为默认</option>
          <option value="NO">非默认</option>
        </select>
        <button
          className="canonical-primary"
          onClick={() => {
            setApplied({ keyword: keyword.trim(), region, status, defaultOnly });
            setCityPage(1);
          }}
        >
          查询
        </button>
        <button onClick={reset}>重置</button>
        <span className="canonical-filter-count">共 {filtered.length} 个运营城市</span>
      </section>
      <section className="canonical-table-card">
        <CanonicalState state={cityState} count={cities.length} noun="城市">
          <div className="canonical-table-wrap">
            <table className="canonical-business-table canonical-city-table">
              <thead>
                <tr>
                  <th>城市</th>
                  <th>城市编码</th>
                  <th>拼音</th>
                  <th>省份</th>
                  <th>首页默认城市</th>
                  <th>开放小程序</th>
                  <th>在线产品</th>
                  <th>排序</th>
                  <th>状态</th>
                  <th>更新时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {filtered
                  .slice((currentCityPage - 1) * cityPageSize, currentCityPage * cityPageSize)
                  .map((city) => {
                    const cityId = textOf(city, 'id');
                    const appCount = appDetails.filter((detail) =>
                      recordArray(detail.cities).some(
                        (binding) =>
                          textOf(binding, 'cityId') === cityId && binding.enabled !== false,
                      ),
                    ).length;
                    const productCount = products.filter(
                      (product) =>
                        textOf(product, 'cityId') === cityId &&
                        textOf(product, 'status') === 'ACTIVE',
                    ).length;
                    return (
                      <tr key={cityId}>
                        <td>
                          <b>{humanText(city, 'name')}</b>
                        </td>
                        <td>{displayBusinessCode(city, 'code')}</td>
                        <td>{humanText(city, 'pinyin')}</td>
                        <td>{cleanFixtureLabel(humanText(childRecord(city.region), 'name'))}</td>
                        <td>
                          {defaultCityIds.has(cityId) ? (
                            <span className="canonical-pill green">默认</span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          {matrixState.loading
                            ? '读取中'
                            : matrixState.error
                              ? '暂不可用'
                              : appCount}
                        </td>
                        <td>
                          {!canViewProducts
                            ? '未授权查看'
                            : productState.loading
                              ? '读取中'
                              : productState.error
                                ? '暂不可用'
                                : productCount}
                        </td>
                        <td>{Number(city.sortOrder ?? 0)}</td>
                        <td>
                          <span className={`canonical-pill ${statusTone(city.status)}`}>
                            {statusLabel(city.status)}
                          </span>
                        </td>
                        <td>{formatDate(city.updatedAt)}</td>
                        <td>
                          <a href={`/admin/cities/${encodeURIComponent(cityId)}`}>
                            {canManage ? '编辑' : '查看'}
                          </a>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          {!filtered.length && <div className="canonical-inline-empty">没有符合筛选条件的城市</div>}
          <div className="canonical-pagination">
            <span>共 {filtered.length} 条</span>
            <button
              type="button"
              disabled={currentCityPage <= 1}
              onClick={() => setCityPage(currentCityPage - 1)}
            >
              上一页
            </button>
            <span className="active" aria-current="page">
              {currentCityPage} / {cityPageCount}
            </span>
            <button
              type="button"
              disabled={currentCityPage >= cityPageCount}
              onClick={() => setCityPage(currentCityPage + 1)}
            >
              下一页
            </button>
            <select
              aria-label="每页条数"
              value={cityPageSize}
              onChange={(event) => {
                setCityPageSize(Number(event.target.value));
                setCityPage(1);
              }}
            >
              <option value={14}>14 条/页</option>
              <option value={28}>28 条/页</option>
              <option value={50}>50 条/页</option>
            </select>
          </div>
        </CanonicalState>
      </section>
      {showCreate && (
        <CityCreateDrawer
          api={api}
          appId={targetAppId}
          regions={regions}
          close={() => setShowCreate(false)}
          done={cityState.reload}
        />
      )}
      {showImport && (
        <CityImportDrawer
          api={api}
          appId={targetAppId}
          regions={regions}
          close={() => setShowImport(false)}
          done={cityState.reload}
        />
      )}
      {showSort && (
        <CitySortDrawer
          api={api}
          cities={cities}
          close={() => setShowSort(false)}
          done={cityState.reload}
        />
      )}
    </div>
  );
}

const SERVICE_MODE_LABELS: Record<string, string> = {
  CHARTER_GUIDE_PACKAGE: '包车 + 向导 + 套餐',
  TRANSFER_TICKET_PACKAGE: '接送 + 票务 + 套餐',
  CARPOOL_GUIDE_PACKAGE: '拼车 + 向导 + 套餐',
};

function serviceModes(product: JsonRecord) {
  const modes = [...new Set(recordArray(product.skus).map((sku) => textOf(sku, 'serviceMode')))];
  return (
    modes.map((mode) => SERVICE_MODE_LABELS[mode] ?? '综合旅游服务').join(' / ') || '套餐待配置'
  );
}

function priceRange(product: JsonRecord) {
  const values = recordArray(product.skus)
    .map((sku) => Number(sku.salePriceCents))
    .filter(Number.isFinite);
  if (!values.length) return '价格待配置';
  const minimum = Math.min(...values);
  const maximum = Math.max(...values);
  return minimum === maximum
    ? formatMoney(minimum)
    : `${formatMoney(minimum)}–${formatMoney(maximum)}`;
}

function ProductCreateDrawer({
  api,
  cities,
  close,
  done,
}: {
  api: AdminApiClient;
  cities: JsonRecord[];
  close: () => void;
  done: () => void;
}) {
  const [cityId, setCityId] = useState('');
  const [productCode, setProductCode] = useState('');
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  return (
    <div className="canonical-drawer" role="dialog" aria-modal="true" aria-label="新增线路产品">
      <div className="canonical-drawer__backdrop" onClick={close} />
      <section>
        <header>
          <h2>新增线路产品</h2>
          <button aria-label="关闭" onClick={close}>
            ×
          </button>
        </header>
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            if (!cityId || !productCode.trim() || !title.trim()) {
              setResult('请完整填写所属城市、产品编码和产品名称');
              return;
            }
            setBusy(true);
            setResult('');
            void api
              .send(`/api/admin/m2/cities/${encodeURIComponent(cityId)}/products`, 'POST', {
                productCode: productCode.trim(),
                title: title.trim(),
                ...(subtitle.trim() ? { subtitle: subtitle.trim() } : {}),
              })
              .then(
                () => {
                  setResult('产品已创建');
                  done();
                  close();
                },
                (error: unknown) =>
                  setResult(
                    error instanceof ApiError && error.isPermissionError
                      ? '当前账号没有新增产品权限'
                      : '创建失败，请检查填写内容后重试',
                  ),
              )
              .finally(() => setBusy(false));
          }}
        >
          <label>
            所属城市
            <select required value={cityId} onChange={(event) => setCityId(event.target.value)}>
              <option value="">请选择城市</option>
              {cities
                .filter((city) => city.status === 'ACTIVE')
                .map((city) => (
                  <option key={textOf(city, 'id')} value={textOf(city, 'id')}>
                    {humanText(city, 'name')}
                  </option>
                ))}
            </select>
          </label>
          <label>
            产品编码
            <input
              required
              maxLength={88}
              value={productCode}
              onChange={(event) => setProductCode(event.target.value)}
              placeholder="请输入唯一产品编码"
            />
          </label>
          <label>
            产品名称
            <input
              required
              maxLength={200}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="请输入对外展示名称"
            />
          </label>
          <label>
            产品副标题
            <input
              value={subtitle}
              onChange={(event) => setSubtitle(event.target.value)}
              placeholder="选填"
            />
          </label>
          <div className="canonical-form-actions">
            <button type="button" onClick={close}>
              取消
            </button>
            <button className="canonical-primary" disabled={busy}>
              {busy ? '创建中…' : '创建产品'}
            </button>
          </div>
          {result && (
            <p className="canonical-form-result" role="status">
              {result}
            </p>
          )}
        </form>
      </section>
    </div>
  );
}

function ProductCopyDrawer({
  api,
  product,
  close,
  done,
}: {
  api: AdminApiClient;
  product: JsonRecord;
  close: () => void;
  done: () => void;
}) {
  const sourceCode = displayBusinessCode(product, 'productCode');
  const [productCode, setProductCode] = useState(`${sourceCode}-COPY`);
  const [title, setTitle] = useState(`${humanText(product, 'title')}（副本）`);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  return (
    <div className="canonical-drawer" role="dialog" aria-modal="true" aria-label="复制线路产品">
      <div className="canonical-drawer__backdrop" onClick={close} />
      <section>
        <header>
          <h2>复制线路产品</h2>
          <button aria-label="关闭" onClick={close}>
            ×
          </button>
        </header>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setBusy(true);
            setResult('');
            void api
              .send(
                `/api/admin/m2/products/${encodeURIComponent(textOf(product, 'id'))}/copy`,
                'POST',
                { productCode, title },
              )
              .then(
                () => {
                  setResult(
                    '产品、套餐、路线、服务项和可复用媒体已复制为草稿；小程序覆盖需单独审核配置。',
                  );
                  done();
                  close();
                },
                (error: unknown) =>
                  setResult(
                    error instanceof Error ? error.message : '复制失败，请检查产品编码后重试',
                  ),
              )
              .finally(() => setBusy(false));
          }}
        >
          <p className="canonical-rule-note">
            复制结果始终是草稿，不会自动上架，也不会复制 App 覆盖和私有上传文件。
          </p>
          <label>
            新产品编码
            <input
              required
              maxLength={88}
              value={productCode}
              onChange={(event) => setProductCode(event.target.value)}
            />
          </label>
          <label>
            新产品名称
            <input
              required
              maxLength={200}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </label>
          <div className="canonical-form-actions">
            <button type="button" onClick={close}>
              取消
            </button>
            <button
              className="canonical-primary"
              disabled={busy || !productCode.trim() || !title.trim()}
            >
              {busy ? '复制中…' : '确认复制'}
            </button>
          </div>
          {result ? (
            <p className="canonical-form-result" role="status">
              {result}
            </p>
          ) : null}
        </form>
      </section>
    </div>
  );
}

export type ProductSelectionTask = 'skus' | 'service-items' | 'booking-refund' | 'publish-check';

export function CanonicalProductListPage({
  api,
  targetAppId,
  task,
}: CanonicalPageProps & { task?: ProductSelectionTask }) {
  const [keyword, setKeyword] = useState('');
  const [city, setCity] = useState('');
  const [tagId, setTagId] = useState('');
  const [status, setStatus] = useState('');
  const [applied, setApplied] = useState({ keyword: '', city: '', tagId: '', status: '' });
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const productPath = useMemo(() => {
    const query = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (applied.keyword) query.set('keyword', applied.keyword);
    if (applied.city) query.set('cityId', applied.city);
    if (applied.tagId) query.set('tagId', applied.tagId);
    if (applied.status) query.set('status', applied.status);
    if (targetAppId) query.set('appId', targetAppId);
    return `/api/admin/m2/products?${query.toString()}`;
  }, [applied, page, targetAppId]);
  const productState = useApi(api, productPath);
  const cityState = useApi(api, '/api/admin/m2/cities');
  const products = unwrapList(productState.data);
  const cities = unwrapList(cityState.data);
  const productPayload = isRecord(productState.data) ? productState.data : {};
  const facets = isRecord(productPayload.facets) ? productPayload.facets : {};
  const cityFacets = recordArray(facets.cities);
  const tagFacets = recordArray(facets.tags);
  const statusFacets = Array.isArray(facets.statuses)
    ? facets.statuses.filter((value): value is string => typeof value === 'string')
    : [];
  const names = useMemo(() => cityNameMap(cityFacets), [cityFacets]);
  const total = Number(productPayload.total ?? products.length);
  const totalPages = Number(productPayload.totalPages ?? (total ? 1 : 0));
  const currentPage = Number(productPayload.page ?? page);
  const hasFilters = Boolean(applied.keyword || applied.city || applied.tagId || applied.status);
  const [showCreate, setShowCreate] = useState(false);
  const [copyProduct, setCopyProduct] = useState<JsonRecord | null>(null);
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
  const [commandBusy, setCommandBusy] = useState(false);
  const [commandResult, setCommandResult] = useState('');
  const [commandFailed, setCommandFailed] = useState(false);
  const [confirmBatchDisable, setConfirmBatchDisable] = useState(false);
  useEffect(() => setSelectedProductIds(new Set()), [productPath]);
  const reset = () => {
    setKeyword('');
    setCity('');
    setTagId('');
    setStatus('');
    setApplied({ keyword: '', city: '', tagId: '', status: '' });
    setPage(1);
  };
  const productCover = (product: JsonRecord) => {
    const media = recordArray(product.media).find(
      (item) =>
        typeof item.url === 'string' && item.mediaType === 'IMAGE' && item.status !== 'DISABLED',
    );
    if (!media) return '';
    const url = typeof media?.url === 'string' ? media.url : '';
    if (!url) return '';
    return url;
  };
  const taskCopy = task
    ? {
        skus: ['选择产品管理套餐', '选择一条线路产品后进入该产品的 SKU 套餐管理。'],
        'service-items': [
          '选择产品配置票务与费用',
          '先选择线路产品，再进入套餐并配置票务与费用服务项。',
        ],
        'booking-refund': [
          '选择产品配置预订退款规则',
          '选择一条线路产品后维护预订限制与退款规则。',
        ],
        'publish-check': ['选择产品执行发布检查', '选择一条线路产品后检查资料完整性和发布条件。'],
      }[task]
    : null;
  const taskTarget = (productId: string) => {
    if (task === 'skus') return `/admin/products/${encodeURIComponent(productId)}/skus`;
    if (task === 'service-items')
      return `/admin/products/${encodeURIComponent(productId)}/skus?task=service-items`;
    if (task === 'booking-refund')
      return `/admin/products/${encodeURIComponent(productId)}/booking-refund`;
    if (task === 'publish-check')
      return `/admin/products/${encodeURIComponent(productId)}/publish-check`;
    return `/admin/products/${encodeURIComponent(productId)}/basic`;
  };
  return (
    <div className="canonical-list-page canonical-product-page">
      <PageTitle
        title={taskCopy?.[0] ?? '线路产品（SPU）'}
        subtitle={taskCopy?.[1] ?? '产品管理 / SPU 列表 · 统一维护产品内容和上下架状态'}
        actions={
          <>
            {task ? <a href="/admin/products">返回产品列表</a> : null}
            <button className="canonical-primary" onClick={() => setShowCreate(true)}>
              新建产品
            </button>
            {selectedProductIds.size ? (
              <ActionButton
                pending={commandBusy}
                pendingLabel="处理中…"
                onClick={() => {
                  setCommandResult('');
                  setCommandFailed(false);
                  setConfirmBatchDisable(true);
                }}
              >
                批量下架（{selectedProductIds.size}）
              </ActionButton>
            ) : (
              <span className="canonical-action-hint">勾选产品后可批量下架</span>
            )}
          </>
        }
      />
      <section className="canonical-product-toolbar">
        <select
          aria-label="城市筛选"
          value={city}
          onChange={(event) => setCity(event.target.value)}
        >
          <option value="">全部城市</option>
          {cityFacets.map((item) => (
            <option key={textOf(item, 'id')} value={textOf(item, 'id')}>
              {humanText(item, 'name')}
            </option>
          ))}
        </select>
        <select
          aria-label="营销标签筛选"
          value={tagId}
          onChange={(event) => setTagId(event.target.value)}
        >
          <option value="">全部营销标签</option>
          {tagFacets.map((tag) => (
            <option key={textOf(tag, 'id')} value={textOf(tag, 'id')}>
              {humanText(tag, 'name')}
            </option>
          ))}
        </select>
        <select
          aria-label="状态筛选"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="">全部状态</option>
          {statusFacets.map((value) => (
            <option key={value} value={value}>
              {value === 'ACTIVE' ? '上架' : value === 'DISABLED' ? '下架' : '草稿'}
            </option>
          ))}
        </select>
        <label className="canonical-search">
          <span>搜索</span>
          <input
            aria-label="搜索产品"
            placeholder="搜索 SPU 名称 / 编码"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
          />
        </label>
        <button
          onClick={() => {
            setPage(1);
            setApplied({ keyword: keyword.trim(), city, tagId, status });
          }}
        >
          查询
        </button>
        <button onClick={reset}>重置</button>
        <a
          className="canonical-export"
          href={
            targetAppId
              ? `/admin/exports?appId=${encodeURIComponent(targetAppId)}`
              : '/admin/exports'
          }
        >
          前往安全导出中心
        </a>
      </section>
      <section className="canonical-table-card canonical-product-card">
        <CanonicalState state={productState} count={products.length} noun="线路产品" allowEmpty>
          {products.length ? (
            <div className="canonical-table-wrap">
              <table className="canonical-business-table canonical-product-table">
                <thead>
                  <tr>
                    <th aria-label="选择" />
                    <th>SPU 编码</th>
                    <th>产品</th>
                    <th>城市</th>
                    <th>分类</th>
                    <th>服务模式</th>
                    <th>价格区间</th>
                    <th>状态</th>
                    <th>排序</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => {
                    const productId = textOf(product, 'id');
                    return (
                      <tr key={productId}>
                        <td>
                          <input
                            type="checkbox"
                            aria-label={`选择 ${humanText(product, 'title')}`}
                            checked={selectedProductIds.has(productId)}
                            onChange={(event) => {
                              const next = new Set(selectedProductIds);
                              if (event.target.checked) next.add(productId);
                              else next.delete(productId);
                              setSelectedProductIds(next);
                            }}
                          />
                        </td>
                        <td>{displayBusinessCode(product, 'productCode')}</td>
                        <td>
                          <div className="canonical-product-cell">
                            <ProductThumbnail
                              api={api}
                              productId={textOf(product, 'id')}
                              scopeKey={targetAppId}
                              src={productCover(product)}
                              alt={`${humanText(product, 'title')}主视觉`}
                            />
                            <div>
                              <b>{cleanFixtureLabel(humanText(product, 'title'))}</b>
                              <small>{humanText(product, 'subtitle')}</small>
                            </div>
                          </div>
                        </td>
                        <td>
                          {names.get(textOf(product, 'cityId')) ??
                            (cityState.loading ? '读取中' : '未标记城市')}
                        </td>
                        <td>
                          {recordArray(product.tags)
                            .map((mapping) => humanText(childRecord(mapping.tag), 'name'))
                            .join(' / ') || '未设置标签'}
                        </td>
                        <td>{serviceModes(product)}</td>
                        <td>
                          <b>{priceRange(product)}</b>
                        </td>
                        <td>
                          <span className={`canonical-pill ${statusTone(product.status)}`}>
                            {product.status === 'ACTIVE'
                              ? '上架'
                              : product.status === 'DISABLED'
                                ? '下架'
                                : '草稿'}
                          </span>
                        </td>
                        <td>{Number(product.sortOrder ?? 0)}</td>
                        <td>
                          <a href={taskTarget(productId)}>{task ? '选择并继续' : '编辑'}</a>
                          <button onClick={() => setCopyProduct(product)}>复制</button>
                          <a
                            href={`/admin/products/${encodeURIComponent(productId)}/publish-check`}
                          >
                            更多
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="canonical-inline-empty canonical-product-empty">
              <strong>{hasFilters ? '没有符合筛选条件的产品' : '暂无线路产品'}</strong>
              <p>
                {hasFilters
                  ? '当前授权范围内没有匹配结果，可以清除筛选后重新查询。'
                  : '当前授权范围内还没有产品，可以新建第一条线路产品。'}
              </p>
              <button
                onClick={hasFilters ? reset : () => setShowCreate(true)}
                className={hasFilters ? undefined : 'canonical-primary'}
              >
                {hasFilters ? '清除筛选' : '新建第一个产品'}
              </button>
            </div>
          )}
          <div className="canonical-rule-note">
            <strong>产品模型：</strong>SPU 负责营销与内容，实际交易参数由多个 SKU
            套餐承载；车型、人数与计价方式均来自套餐配置。
          </div>
          <div className="canonical-pagination">
            <span>共 {total} 条</span>
            <button
              disabled={productState.loading || currentPage <= 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
            >
              上一页
            </button>
            <span className="active" aria-current="page">
              {currentPage} / {Math.max(totalPages, 1)}
            </span>
            <button
              disabled={productState.loading || totalPages === 0 || currentPage >= totalPages}
              onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
            >
              下一页
            </button>
          </div>
        </CanonicalState>
      </section>
      {showCreate && (
        <ProductCreateDrawer
          api={api}
          cities={cities}
          close={() => setShowCreate(false)}
          done={productState.reload}
        />
      )}
      {copyProduct ? (
        <ProductCopyDrawer
          api={api}
          product={copyProduct}
          close={() => setCopyProduct(null)}
          done={productState.reload}
        />
      ) : null}
      <ConfirmDialog
        open={confirmBatchDisable}
        title="确认批量下架"
        description={`即将下架已选择的 ${selectedProductIds.size} 个产品。下架后前台将停止展示，确定继续吗？`}
        confirmLabel="确认下架"
        dangerous
        pending={commandBusy}
        onCancel={() => setConfirmBatchDisable(false)}
        onConfirm={() => {
          const productIds = [...selectedProductIds];
          setCommandBusy(true);
          setCommandResult('');
          setCommandFailed(false);
          void api
            .send('/api/admin/m2/products/batch-disable', 'POST', { productIds })
            .then(
              () => {
                setCommandResult(`已下架 ${productIds.length} 个产品`);
                setSelectedProductIds(new Set());
                setConfirmBatchDisable(false);
                productState.reload();
              },
              (error: unknown) => {
                setCommandFailed(true);
                setCommandResult(error instanceof Error ? error.message : '批量下架失败');
              },
            )
            .finally(() => setCommandBusy(false));
        }}
      />
      {commandResult ? (
        <Notice tone={commandFailed ? 'error' : 'success'}>{commandResult}</Notice>
      ) : null}
    </div>
  );
}
